import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Input } from 'antd';
import { motion, useReducedMotion } from 'framer-motion';
import { Lock } from 'lucide-react';
import SectionCard from '../SectionCard';
import { useRoleEditorContext } from '../RoleEditorContext';
import PermissionMatrix from '../../PermissionMatrix';
import { SOVEREIGN } from '../../../../epc/utils/epcProjectVisual';
import type { RoleEditorFormValues } from '../types';

/**
 * Step 5 of 6 — Permissions.
 *
 * Wraps the existing <PermissionMatrix>. The matrix renders
 * <EpcProjectsTierAutoGrant> as a special row when the epcProject module is
 * in scope — that card is read-only by default (driven by the Step 2 tag)
 * with an Advanced disclosure for manual overrides.
 *
 * Adds a soft-green summary bar with live module/action counters and a
 * module-filter search input that drives the matrix's `search` prop.
 */
export default function PermissionsSection() {
  const { setValue, watch } = useFormContext<RoleEditorFormValues>();
  const { matrixConfig, isReadonly, setActiveSection } = useRoleEditorContext();
  const reduceMotion = useReducedMotion();

  const [permSearch, setPermSearch] = useState('');

  const permissions = watch('permissions') ?? {};
  const epcTag = watch('tag');

  // Live counters — drive the count-up animation via motion's key change.
  const { moduleCount, actionCount } = useMemo(() => {
    let modules = 0;
    let actions = 0;
    for (const arr of Object.values(permissions)) {
      if (Array.isArray(arr) && arr.length) {
        modules += 1;
        actions += arr.length;
      }
    }
    return { moduleCount: modules, actionCount: actions };
  }, [permissions]);

  const handleMatrixChange = (next: Record<string, string[]>) => {
    setValue('permissions', next, { shouldDirty: true, shouldValidate: true });
  };

  const noModules = matrixConfig && matrixConfig.modules.length === 0;

  return (
    <SectionCard
      icon={Lock}
      title="Permissions"
      sub="Module-action grants. EPC tiers go here too."
      step={5}
      totalSteps={6}
    >
      <div className="space-y-4">
        {/* Top summary bar */}
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl px-4 py-3"
          style={{
            background: 'rgba(31,90,71,0.06)',
            border: `1px solid ${SOVEREIGN.cardBorder}`,
          }}
        >
          <div className="text-[12.5px]" style={{ color: SOVEREIGN.ink }}>
            <motion.span
              key={moduleCount}
              initial={reduceMotion ? false : { opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="font-bold tabular-nums"
              style={{ color: SOVEREIGN.bottle }}
            >
              {moduleCount}
            </motion.span>{' '}
            <span style={{ color: SOVEREIGN.kpiCaption }}>modules with access</span>{' '}
            <span style={{ color: SOVEREIGN.barTrack }}>·</span>{' '}
            <motion.span
              key={actionCount}
              initial={reduceMotion ? false : { opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="font-bold tabular-nums"
              style={{ color: SOVEREIGN.bottle }}
            >
              {actionCount}
            </motion.span>{' '}
            <span style={{ color: SOVEREIGN.kpiCaption }}>actions selected</span>
          </div>
          <Input.Search
            size="small"
            allowClear
            placeholder="Filter modules…"
            value={permSearch}
            onChange={(e) => setPermSearch(e.target.value)}
            onSearch={(v) => setPermSearch(v)}
            style={{ maxWidth: 240 }}
            disabled={!!noModules}
          />
        </div>

        {/* Permission matrix (embeds the EPC tier panel as its own row when
            the epcProject module is in scope — no duplicate panel needed) */}
        {matrixConfig && matrixConfig.modules.length > 0 && (
          <PermissionMatrix
            value={permissions}
            onChange={handleMatrixChange}
            config={matrixConfig}
            disabled={isReadonly}
            search={permSearch}
            epcTag={epcTag}
            onJumpToDepartmentStep={() => setActiveSection('department')}
          />
        )}

        {/* Empty state with inline flat illustration */}
        {noModules && <PermissionsEmptyState />}
      </div>
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/*  Empty state                                                                */
/* -------------------------------------------------------------------------- */

function PermissionsEmptyState() {
  return (
    <div
      className="flex flex-col items-center justify-center rounded-2xl px-6 py-10 text-center"
      style={{
        border: `2px dashed ${SOVEREIGN.cardBorder}`,
        background: SOVEREIGN.inputBg,
      }}
    >
      <EmptyStateIllustration />
      <h4
        className="mt-4 text-sm font-semibold"
        style={{ color: SOVEREIGN.title }}
      >
        No modules available for this department
      </h4>
      <p
        className="mt-1 max-w-sm text-[12px]"
        style={{ color: SOVEREIGN.kpiCaption }}
      >
        Check the feature catalog or pick a different department.
      </p>
    </div>
  );
}

function EmptyStateIllustration() {
  return (
    <svg
      width="200"
      height="140"
      viewBox="0 0 200 140"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      role="img"
    >
      <style>{`
        @keyframes role-empty-spin { to { transform: rotate(360deg); } }
        .turbine-blades {
          transform-origin: 158px 38px;
          animation: role-empty-spin 6s linear infinite;
        }
        .sun-rays {
          transform-origin: 38px 38px;
          animation: role-empty-spin 18s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .turbine-blades, .sun-rays { animation: none; }
        }
      `}</style>

      {/* Background */}
      <rect x="0" y="0" width="200" height="140" rx="12" fill="#F0EDE4" />

      {/* Sun + rays (left) */}
      <g className="sun-rays">
        <circle cx="38" cy="38" r="12" fill="#F4C24D" />
        <g stroke="#F4C24D" strokeWidth="2" strokeLinecap="round">
          <line x1="38" y1="18" x2="38" y2="24" />
          <line x1="38" y1="52" x2="38" y2="58" />
          <line x1="18" y1="38" x2="24" y2="38" />
          <line x1="52" y1="38" x2="58" y2="38" />
          <line x1="24" y1="24" x2="28" y2="28" />
          <line x1="48" y1="48" x2="52" y2="52" />
          <line x1="52" y1="24" x2="48" y2="28" />
          <line x1="28" y1="48" x2="24" y2="52" />
        </g>
      </g>

      {/* Wind turbine (right) */}
      <line
        x1="158"
        y1="38"
        x2="158"
        y2="105"
        stroke="#A3CD39"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <g className="turbine-blades">
        <ellipse cx="158" cy="22" rx="3" ry="14" fill="#F08A3E" />
        <ellipse
          cx="158"
          cy="22"
          rx="3"
          ry="14"
          fill="#F08A3E"
          transform="rotate(120 158 38)"
        />
        <ellipse
          cx="158"
          cy="22"
          rx="3"
          ry="14"
          fill="#F08A3E"
          transform="rotate(240 158 38)"
        />
        <circle cx="158" cy="38" r="3" fill="#339274" />
      </g>

      {/* Lock body (centre) */}
      <rect
        x="74"
        y="68"
        width="52"
        height="44"
        rx="6"
        fill="#339274"
        stroke="#29795f"
        strokeWidth="1.5"
      />
      <path
        d="M84 68 V58 a16 16 0 0 1 32 0 V68"
        fill="none"
        stroke="#339274"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <circle cx="100" cy="88" r="4.5" fill="#A3CD39" />
      <rect x="98" y="88" width="4" height="10" rx="1.5" fill="#A3CD39" />

      {/* Ground line */}
      <line
        x1="12"
        y1="120"
        x2="188"
        y2="120"
        stroke="#D6D1C0"
        strokeWidth="1.5"
        strokeDasharray="3 4"
      />
    </svg>
  );
}
